/**
 * This file is used for the actions for Authentication.
 * @exports Actions
 * @author {Cognizant Technology Solution}
 * @flow
 */
/**
 * Manages Okta authentication token storage and derived user details in Redux state.
 */
import { createSlice } from '@reduxjs/toolkit';
import appConfig from '../../appConfig';

const initialState = {};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    /**
     * Stores Okta token payload data and derives user profile fields from token claims.
     */
    setAuthKey(state, action) {
      const rawValue = action.payload['okta-token-storage'];
      if (!rawValue || typeof rawValue !== 'string' || rawValue.length === 0) {
        // Okta's TokenManager writes multiple keys via custom storage (cache, tokens, etc.).
        // Only `okta-token-storage` holds id token claims used for userDetails.group.
        return {
          ...state,
          ...action.payload
        };
      }

      let parsedToken = null;
      try {
        parsedToken = JSON.parse(rawValue);
      } catch (e) {
        console.log('authSlice: Failed to parse okta-token-storage', e);
      }

      const claims = (parsedToken?.idToken?.claims) || {};
      const oktaUserGroupMapping = claims.groups;
      let userGroup = '';
      if (Array.isArray(oktaUserGroupMapping)) {
        // Choose the highest-precedence configured group present in the Okta claims.
        userGroup = appConfig.userGroupPrecedence
          .find((group) => oktaUserGroupMapping.includes(group));
      }
      const userDetails = {
        group: userGroup,
        email: claims.email,
        name: claims.name,
        userID: claims.legacy_username
      };

      const storageValue = parsedToken ? JSON.stringify(parsedToken) : '{}';
      try {
        // Keep browser storage aligned with the parsed token state consumed by auth flows.
        localStorage.setItem('okta-token-storage', storageValue);
      } catch (e) {
        console.log('authSlice: Failed to set localStorage', e);
      }

      return {
        ...state,
        ...action.payload,
        parsedToken,
        userDetails,
        isMetricsVisible: appConfig.enableMultiDashboard
      };
    },
    /**
     * Resets authentication state back to its empty initial shape.
     */
    resetToInitialState() {
      return { ...initialState };
    }
  }
});

export const {
  setAuthKey,
  resetToInitialState
} = authSlice.actions;

export default authSlice.reducer;
